<template>
  <div class="code" :class="[size]">
    <input
      v-for="i in length"
      ref="inputRefs"
      :key="`code-${i}`"
      validation="matches:/^[0-9A-Za-z]{1}$/"
      name="code"
      class="code-input"
      required
      @keydown.left="handleKeyMove(i - 1, -1)"
      @keydown.right="handleKeyMove(i - 1, 1)"
      @keydown.backspace="handleKeyDelete(i - 1)"
      @input="(e) => handleChange(e, i - 1)"
    />
  </div>
</template>
<script setup lang="ts">
interface Props {
  code: string;
  length?: number;
  inputFormatter?: (t: string) => string;
  defaultCode?: string;
  hasDefaultCode?: boolean;
  size?: 'small' | 'normal';
}

const props = defineProps({
  code: null,
  length: { default: 5 },
  inputFormatter: { type: Function, default: (t: string) => {
    return t.replace(/[^a-z0-9A-Z]/g, '').toUpperCase();
  } },
  defaultCode: null,
  hasDefaultCode: { type: Boolean },
  size: null
});

const emit = defineEmits(["update:code"]);

const inputValue = reactive(new Array<string>(props.length));

watchEffect(() => {
  const newValue = inputValue.join('');
  if (newValue.length === props.length) {
    emit('update:code', newValue);
  } else {
    emit('update:code', '');
  }
});

const inputRefs = ref([]);

const handleChange = (e: Event, index: number) => {
  let currentValue = (e.target as HTMLInputElement).value;
  if (currentValue.length > 1 && currentValue.charAt(0) === inputValue[index]) {
    currentValue = [...currentValue].slice(1).join('');
  }

  const [first, ...rest] = currentValue;
  let value = first ?? ''; // first will be undefined when backspace was entered, so set the input to ""
  if (value.length === 2 && inputValue[index] === [...value][0]) {
    value = [...value].slice(1).join('');
  }
  const formattedValue = props.inputFormatter(value);

  inputValue[index] = formattedValue;
  const inputRef = inputRefs.value[index];
  if (inputRef) {
    inputRef.value = inputValue[index];
  }

  if (formattedValue) {
    handleKeyMove(index, +1);
  }

  const lastInputBox = index === props.length - 1;
  const didInsertContent = first !== undefined;
  if (didInsertContent && !lastInputBox && rest.length > 0) {
    inputRefs.value[index + 1].focus();
    inputRefs.value[index + 1].value = rest.join('');
    inputRefs.value[index + 1].dispatchEvent(new Event('input'));
  }
};

const handleKeyDelete = (index) => {
  const elt = inputRefs.value[index];
  if (elt.value === '' && index > 0) {
    inputRefs.value[index - 1].focus();
  }
};
const handleKeyMove = (index, sens) => {
  if (index === 0 && sens === -1) return;
  if (index === props.length - 1 && sens === 1) return;
  inputRefs.value[index + sens].focus();
};

onMounted(() => {
  if (props.hasDefaultCode) {
    props.defaultCode.split('').forEach((val, index) => {
      inputRefs.value[index].value = val;
      inputValue[index] = val;
    });
  }
});
</script>

<style lang="scss" scoped>
.code {
  display: flex;
  align-self: center;
  justify-content: space-between;
  width: 100%;
  input {
    width: 4.5rem;
    padding: 2rem 0.6rem;
    border: 0.2rem solid var(--color_neutral_100);
    border-radius: 0.8rem;
    color: var(--color_secondary);
    text-align: center;
    @include font('l', 700);
    &:focus {
      border: 0.2rem solid var(--color_secondary);
      outline: none;
    }
  }

  &.small {
    input {
      width: 3rem;
      @include font('m', 400);

      padding: 1.2rem 0.3rem;
    }
  }
}
</style>
